<template>
  <div class="volunteer-dashboard">
    <dashboard-banner />

    <div
      v-if="downtimeBannerMessage"
      class="dashboard-notice"
      :class="'dashboard-notice--info'"
    >
      <a href="https://upchieve.statuspage.io" target="_blank">{{
        downtimeBannerMessage
      }}</a>
    </div>

    <div class="volunteer-dashboard__body">
      <template v-if="user.isApproved && user.isOnboarded">
        <div class="dashboard-card">
          <div class="students-waiting">
            <web-notifications-button class="notifications-button" />
            <div class="dashboard-card__title">
              {{ $t('dashboard.volunteerDashboard.cardTitle') }}
            </div>
            <div v-if="isSessionAlive" class="rejoin-session-container">
              <button
                class="btn rejoinSessionBtn"
                type="button"
                @click.prevent="rejoinHelpSession()"
              >
                {{ $t('dashboard.volunteerDashboard.aliveButtonText') }}
              </button>
            </div>
            <div v-else>
              <div class="dashboard-card__subtitle">
                {{ $t('dashboard.volunteerDashboard.cardSubtitle') }}
              </div>
              <list-sessions />
            </div>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="dashboard-card__title">
            {{ $t('dashboard.volunteerDashboard.secondCardTitle') }}
          </div>

          <div class="volunteer-impact">
            <div class="volunteer-impact__stats">
              <loader v-if="isLoadingImpactSummary" class="loader--center" />
              <div v-else>
                <div
                  v-for="(stat, statIndex) in impactStats"
                  :key="statIndex"
                  class="volunteer-impact__stat"
                >
                  <div class="volunteer-impact__stat-label">
                    {{ stat.label }}:
                  </div>
                  <div class="volunteer-impact__stat-value">
                    {{ stat.value }}
                  </div>
                </div>
              </div>
              <div
                v-if="isCustomVolunteerPartner"
                class="volunteer-impact__last-updated"
              >
                {{ lastUpdated }}
              </div>
            </div>
          </div>
          <div class="dashboard-card__link">
            <a
              class="track-hours-link"
              :href="hourTrackingGuide"
              target="_blank"
              rel="noopener noreferrer"
              >{{ $t('dashboard.volunteerDashboard.secondCardLink') }}
              <arrow-right-icon
                v-if="dir == 'ltr'"
                class="ButtonTemplate-icon arrow-icon"
              />
              <arrow-left-icon
                v-else-if="dir == 'rtl'"
                class="ButtonTemplate-icon arrow-icon"
              />
            </a>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="!user.isApproved" class="dashboard-card">
          <div class="dashboard-card__icon">
            <verification-icon />
          </div>
          <div class="dashboard-card__title">
            {{ $t('dashboard.volunteerDashboard.verificationCardTitle') }}
          </div>
          <div class="dashboard-card__subtitle">
            {{ approvalCardSubheader }}
          </div>
          <template v-if="!user.volunteerPartnerOrg">
            <account-action
              v-for="accountAction in openVolunteerApprovalAccountActions"
              :key="accountAction.title"
              :title="accountAction.title"
              :subtitle="accountAction.subtitle"
              :status="accountAction.status"
              :icon="accountAction.icon"
              @click.native="accountAction.clickFn"
            />
          </template>
          <template v-else>
            <account-action
              v-for="accountAction in partnerVolunteerApprovalAccountActions"
              :key="accountAction.title"
              :title="accountAction.title"
              :subtitle="accountAction.subtitle"
              :status="accountAction.status"
              :icon="accountAction.icon"
              @click.native="accountAction.clickFn"
            />
          </template>
        </div>
        <div class="dashboard-card">
          <div class="dashboard-card__icon">
            <onboarding-icon />
          </div>
          <div class="dashboard-card__title">
            {{ $t('dashboard.volunteerDashboard.onboardingCard.title') }}
          </div>
          <div class="dashboard-card__subtitle">
            {{ $t('dashboard.volunteerDashboard.onboardingCard.subtitle') }}
          </div>

          <account-action
            v-for="accountAction in onboardingAccountActions"
            :key="accountAction.title"
            :title="accountAction.title"
            :subtitle="accountAction.subtitle"
            :status="accountAction.status"
            :icon="accountAction.icon"
            @click.native="accountAction.clickFn"
          />
        </div>
      </template>
    </div>

    <photo-upload-modal
      v-if="showPhotoUploadModal"
      :closeModal="togglePhotoUploadModal"
    />

    <volunteer-welcome-modal
      v-if="showWelcomeModal"
      :closeModal="toggleWelcomeModal"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import ListSessions from './ListSessions.vue'
import DashboardBanner from '../DashboardBanner'
import AccountAction from './AccountAction.vue'
import PhotoUploadModal from './PhotoUploadModal.vue'
import VolunteerWelcomeModal from '@/views/DashboardView/VolunteerDashboard/VolunteerWelcomeModal.vue'
import PersonCardIcon from '@/assets/person-card.svg'
import PersonIcon from '@/assets/person.svg'
import CalendarIcon from '@/assets/calendar.svg'
import CertificationIcon from '@/assets/certification.svg'
import VerificationIcon from '@/assets/verification.svg'
import OnboardingIcon from '@/assets/onboarding.svg'
import TrainingIcon from '@/assets/training_icon.svg'
import WebNotificationsButton from '@/components/WebNotificationsButton.vue'
import ArrowLeftIcon from '@/assets/left-arrow.svg'
import ArrowRightIcon from '@/assets/arrow.svg'
import NetworkService from '../../../services/NetworkService'
import config from '../../../config'
import Loader from '@/components/Loader.vue'
import { VERIFICATION_METHOD } from '@/consts'
import i18n from '@/i18n'

const defaultHeaderData = {
  component: 'DefaultHeader',
}

const rejoinHeaderData = {
  component: 'RejoinSessionHeader',
}

export default {
  name: 'volunteer-dashboard',
  components: {
    ListSessions,
    DashboardBanner,
    AccountAction,
    PhotoUploadModal,
    VerificationIcon,
    OnboardingIcon,
    VolunteerWelcomeModal,
    WebNotificationsButton,
    ArrowLeftIcon,
    ArrowRightIcon,
    Loader,
  },

  watch: {
    isSessionAlive(isAlive) {
      if (!isAlive) {
        this.$store.dispatch('app/header/show', defaultHeaderData)
      } else {
        this.$store.dispatch('app/header/show', rejoinHeaderData)
      }
    },
    allSubjectNames: {
      handler(currValue, prevValue) {
        const isNowLoaded = currValue.length && !prevValue?.length
        if (isNowLoaded) this.initImpactSummary()
      },
      immediate: true,
    },
  },
  async created() {
    if (this.isFirstDashboardVisit) {
      this.toggleWelcomeModal()
    }

    if (this.isSessionAlive) {
      this.$store.dispatch('app/header/show', rejoinHeaderData)
    } else if (
      !this.user.emailVerified &&
      this.isSmsVerificationEnabledOnSignupFlow
    ) {
      this.$store.dispatch('app/header/show', {
        component: 'VerificationHeader',
        data: {
          verificationMethod: VERIFICATION_METHOD.EMAIL,
          phoneOrEmailToVerify: this.user.email,
        },
      })
    }

    // TODO: move globally to show banner in all pages
    if (this.user && this.user.isBanned) {
      this.$store.dispatch('app/header/show', {
        component: 'BannedHeader',
      })
    }
  },
  data() {
    return {
      showPhotoUploadModal: false,
      showWelcomeModal: false,
      lastUpdated: '',
      impactStats: {},
      isLoadingImpactSummary: true,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
      isFirstDashboardVisit: state => state.user.isFirstDashboardVisit,
    }),
    ...mapGetters({
      isSessionAlive: 'user/isSessionAlive',
      sessionPath: 'user/sessionPath',
      hasCertification: 'user/hasCertification',
      hasSelectedAvailability: 'user/hasSelectedAvailability',
      downtimeBannerMessage: 'featureFlags/downtimeBannerMessage',
      isFilterActiveSubjectsActive: 'featureFlags/isFilterActiveSubjectsActive',
      allSubjectNames: 'subjects/allSubtopicNames',
      isSmsVerificationEnabledOnSignupFlow:
        'featureFlags/isSmsVerificationEnabledOnSignupFlow',
      dir: 'app/direction',
    }),

    isCustomVolunteerPartner() {
      return config.customVolunteerPartnerOrgs.some(
        org => org === this.user.volunteerPartnerOrg
      )
    },

    isNewVolunteer() {
      return !this.user.pastSessions || !this.user.pastSessions.length
    },

    photoIdAction() {
      switch (this.user.photoIdStatus) {
        case 'EMPTY':
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.photoIdAction.EMPTY.subtitle'
            ),
            status: 'DEFAULT',
          }
        case 'SUBMITTED':
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.photoIdAction.SUBMITTED.subtitle'
            ),
            status: 'PENDING',
          }
        case 'APPROVED':
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.photoIdAction.APPROVED.subtitle'
            ),
            status: 'COMPLETED',
          }
        case 'REJECTED':
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.photoIdAction.REJECTED.subtitle'
            ),
            status: 'ERROR',
          }
        default:
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.photoIdAction.default.subtitle'
            ),
            status: 'DEFAULT',
          }
      }
    },

    availabilityAction() {
      if (this.hasSelectedAvailability)
        return {
          subtitle: this.$root.$t(
            'dashboard.volunteerDashboard.availabilityAction.hasSelectedAvailability.subtitle'
          ),
          status: 'COMPLETED',
        }

      return {
        subtitle: this.$root.$t(
          'dashboard.volunteerDashboard.availabilityAction.default.subtitle'
        ),
        status: 'DEFAULT',
      }
    },

    certificationAction() {
      for (let cert in this.user.certifications) {
        // skip certification for check for required training
        if (cert === 'upchieve101') continue
        if (this.user.certifications[cert].passed)
          return {
            subtitle: this.$root.$t(
              'dashboard.volunteerDashboard.certificationAction.passed.subtitle'
            ),
            status: 'COMPLETED',
          }
      }
      return {
        subtitle: this.$root.$t(
          'dashboard.volunteerDashboard.certificationAction.default.subtitle'
        ),
        status: 'DEFAULT',
      }
    },

    trainingAction() {
      const passedQuiz = this.user.certifications.upchieve101.passed
      if (passedQuiz)
        return {
          subtitle: this.$root.$t(
            'dashboard.volunteerDashboard.trainingAction.passedQuiz.subtitle'
          ),
          status: 'COMPLETED',
        }

      const startedCourse = this.user.trainingCourses.upchieve101.progress > 0
      if (startedCourse)
        return {
          subtitle: this.$root.$t(
            'dashboard.volunteerDashboard.trainingAction.startedCourse.subtitle'
          ),
          status: 'PENDING',
        }

      return {
        subtitle: this.$root.$t(
          'dashboard.volunteerDashboard.trainingAction.default.subtitle'
        ),
        status: 'DEFAULT',
      }
    },

    backgroundInfoAction() {
      if (this.hasCompletedBackgroundInfo)
        return {
          subtitle: this.$root.$t(
            'dashboard.volunteerDashboard.backgroundInfoAction.hasCompleted.subtitle'
          ),
          status: 'COMPLETED',
        }

      return {
        subtitle: this.$root.$t(
          'dashboard.volunteerDashboard.backgroundInfoAction.hasNotCompleted.subtitle'
        ),
        status: 'DEFAULT',
      }
    },

    hasCompletedBackgroundInfo() {
      return (
        this.user.occupation &&
        this.user.occupation.length > 0 &&
        this.user.country
      )
    },

    approvalCardSubheader() {
      if (this.user.volunteerPartnerOrg)
        return this.$root.$t(
          'dashboard.volunteerDashboard.approvalCardSubheader.volunteer'
        )

      return this.$root.$t(
        'dashboard.volunteerDashboard.approvalCardSubheader.student'
      )
    },
    openVolunteerApprovalAccountActions() {
      const accountActions = [
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.openVolunteerApprovalAccountActions.title-1'
          ),
          subtitle: this.backgroundInfoAction.subtitle,
          status: this.backgroundInfoAction.status,
          clickFn: this.goToBackgroundInfo,
          icon: PersonIcon,
          priority: this.addSortPriorityNum(this.backgroundInfoAction.status),
        },
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.openVolunteerApprovalAccountActions.title-2'
          ),
          subtitle: this.photoIdAction.subtitle,
          status: this.photoIdAction.status,
          clickFn: this.togglePhotoUploadModal,
          icon: PersonCardIcon,
          priority: this.addSortPriorityNum(this.photoIdAction.status),
        },
      ]

      return accountActions.sort((a, b) => a.priority - b.priority)
    },

    partnerVolunteerApprovalAccountActions() {
      const accountActions = [
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.partnerVolunteerAccountActions.object-1.title'
          ),
          subtitle: this.backgroundInfoAction.subtitle,
          status: this.backgroundInfoAction.status,
          clickFn: this.goToBackgroundInfo,
          icon: PersonIcon,
          priority: this.addSortPriorityNum(this.backgroundInfoAction.status),
        },
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.partnerVolunteerAccountActions.object-2.title'
          ),
          // @todo: change copy for subtitle
          subtitle: this.$root.$t(
            'dashboard.volunteerDashboard.partnerVolunteerAccountActions.object-2.subtitle'
          ),
          status: 'COMPLETED',
          clickFn: () => {},
          icon: PersonCardIcon,
          priority: this.addSortPriorityNum(
            this.$root.$t(
              'dashboard.volunteerDashboard.accountActions.object-2.priority'
            )
          ),
        },
      ]

      return accountActions.sort((a, b) => a.priority - b.priority)
    },

    onboardingAccountActions() {
      const onboardingActions = [
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.onboardingAccountActions.title-1'
          ),
          subtitle: this.trainingAction.subtitle,
          status: this.trainingAction.status,
          clickFn: this.clickUpchieve101Action,
          icon: TrainingIcon,
          priority: this.addSortPriorityNum(this.trainingAction.status),
        },
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.onboardingAccountActions.title-2'
          ),
          subtitle: this.availabilityAction.subtitle,
          status: this.availabilityAction.status,
          clickFn: this.clickAvailabilityAction,
          icon: CalendarIcon,
          priority: this.addSortPriorityNum(this.availabilityAction.status),
        },
        {
          title: this.$root.$t(
            'dashboard.volunteerDashboard.onboardingAccountActions.title-3'
          ),
          subtitle: this.certificationAction.subtitle,
          status: this.certificationAction.status,
          clickFn: this.clickCertificationAction,
          icon: CertificationIcon,
          priority: this.addSortPriorityNum(this.certificationAction.status),
        },
      ]
      return onboardingActions.sort((a, b) => a.priority - b.priority)
    },

    hourTrackingGuide() {
      if (this.isCustomVolunteerPartner)
        return 'https://cdn.upchieve.org/docs/Verizon-Volunteer-Hour-Tracking-Resource.pdf'
      return 'https://upc-training-materials.s3.us-east-2.amazonaws.com/volunteer-hour-tracking-guide.pdf'
    },
  },
  methods: {
    rejoinHelpSession() {
      const path = this.sessionPath
      if (path) {
        this.$router.push(path)
      } else {
        this.$router.push(`/${i18n.locale}/dashboard`)
      }
    },

    showOnboardingModal() {
      this.$store.dispatch('app/modal/show', {
        component: 'VolunteerOnboardingModal',
        data: {
          alertModal: true,
          acceptText: this.$root.$t(
            'dashboard.volunteerDashboard.showOnboardingModalAcceptText'
          ),
        },
      })
    },
    toggleWelcomeModal() {
      this.showWelcomeModal = !this.showWelcomeModal
    },
    togglePhotoUploadModal() {
      this.showPhotoUploadModal = !this.showPhotoUploadModal
    },
    clickAvailabilityAction() {
      this.$router.push(`/${i18n.locale}/calendar`)
    },
    clickCertificationAction() {
      this.$router.push(`/${i18n.locale}/training`)
    },
    clickUpchieve101Action() {
      this.$router.push(`/${i18n.locale}/training/course/upchieve101`)
    },
    goToBackgroundInfo() {
      this.$router.push(`/${i18n.locale}/background-information`)
    },
    addSortPriorityNum(status) {
      if (status === 'COMPLETED') return 0
      if (status === 'ERROR') return 1
      if (status === 'PENDING') return 2
      if (status === 'PROGRESS') return 3
      if (status === 'DEFAULT') return 4
    },
    async getLastUpdated() {
      const res = await NetworkService.getVolunteerLastUpdated()
      if (res.data.err) {
        return this.$root.$t(
          'dashboard.volunteerDashboard.getLastUpdated.errorCotext'
        )
      }
      const lastUpdated = res.data.lastUpdated
      return `${this.$root.$t(
        'dashboard.volunteerDashboard.getLastUpdated.lastUpdated'
      )} ${lastUpdated}`
    },
    getImpactStats() {
      const user = this.$store.state.user.user
      // (1) Hours selected
      const userHasSchedule = _.chain(user)
        .get('availability.Thursday.5p')
        .isBoolean()
        .value()

      let numHoursSelected = 0

      if (userHasSchedule) {
        numHoursSelected = _.reduce(
          user.availability,
          (weeklyHourCount, dayHours) => {
            // Tally up num hours for each day
            const hoursSelectedForDay = _.reduce(
              dayHours,
              (dailyHourCount, hourVal) => {
                // Add 1 if hour val is true
                return dailyHourCount + (hourVal ? 1 : 0)
              },
              0
            )

            return weeklyHourCount + hoursSelectedForDay
          },
          0
        )
      }

      // (3) Requests filled
      const numRequestsFilled = _.get(user, 'pastSessions.length', '--')

      // (4) Hours tutored
      const numHoursTutored = Number(this.user.hoursTutored) || '0'

      // (5) Elapsed availability
      const numElapsedAvailabilityHours = user.elapsedAvailability

      return [
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.label-1'
          ),
          value: `${numHoursSelected} ${this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.value-1'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.label-2'
          ),
          value: `${this.user.totalQuizzesPassed} ${this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.value-2'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.label-3'
          ),
          value: `${numRequestsFilled} ${this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.value-3'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.label-4'
          ),
          value: `${numHoursTutored} ${this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.value-4'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.label-5'
          ),
          value: `${numElapsedAvailabilityHours} ${this.$root.$t(
            'dashboard.volunteerDashboard.impactStats.value-4'
          )}`,
        },
      ]
    },
    getCustomImpactStats() {
      const user = this.$store.state.user.user
      // (1) Hours selected
      const userHasSchedule = _.chain(user)
        .get('availability.Thursday.5p')
        .isBoolean()
        .value()

      let numHoursSelected = 0

      if (userHasSchedule) {
        numHoursSelected = _.reduce(
          user.availability,
          (weeklyHourCount, dayHours) => {
            // Tally up num hours for each day
            const hoursSelectedForDay = _.reduce(
              dayHours,
              (dailyHourCount, hourVal) => {
                // Add 1 if hour val is true
                return dailyHourCount + (hourVal ? 1 : 0)
              },
              0
            )

            return weeklyHourCount + hoursSelectedForDay
          },
          0
        )
      }

      // (3) Requests filled
      const numRequestsFilled = _.get(user, 'pastSessions.length', '--')

      // (4) Hours volunteered
      const numHoursVolunteered = Number(user.totalVolunteerHours) || '--'

      return [
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.label-1'
          ),
          value: `${numHoursSelected} ${this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.value-1'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.label-2'
          ),
          value: `${this.user.totalQuizzesPassed} ${this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.value-2'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.label-3'
          ),
          value: `${numRequestsFilled} ${this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.value-3'
          )}`,
        },
        {
          label: this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.label-4'
          ),
          value: `${numHoursVolunteered} ${this.$root.$t(
            'dashboard.volunteerDashboard.customImpactStats.value-4'
          )}`,
        },
      ]
    },
    async initImpactSummary() {
      try {
        if (this.isCustomVolunteerPartner) {
          this.impactStats = await this.getCustomImpactStats()
          this.lastUpdated = await this.getLastUpdated()
        } else this.impactStats = await this.getImpactStats()
      } finally {
        this.isLoadingImpactSummary = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn {
  height: 60px;
  background-color: #16d2aa;
  border: none;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: white;
  line-height: 40px;

  &:hover {
    background-color: #16d2aa;
  }

  &:disabled {
    color: white;
  }

  &.rejoinSessionBtn {
    border-radius: 30px;
    width: 300px;
    margin-top: 25px;
  }
}

.volunteer-dashboard {
  @include flex-container(column);
  padding: 40px 15px;

  @include breakpoint-above('medium') {
    display: inline-flex;
    min-width: 100%;
    padding: 40px;
  }

  &__body {
    @include child-spacing(top, 16px);
    @include child-spacing(right, 0);
    margin-top: 40px;

    @include breakpoint-above('huge') {
      @include child-spacing(top, 0);
      @include child-spacing(right, 40px);

      @include flex-container(row);

      & > * {
        flex-basis: 50%;
      }
    }
  }
}

.dashboard-card {
  background: #fff;
  border-radius: 8px;
  padding: 40px 0 24px;

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
  }

  &__title {
    margin-bottom: 4px;
    font-size: 24px;
    font-weight: 500;
    line-height: 1.25;
    text-align: center;
  }

  &__subtitle {
    font-size: 16px;
    color: $c-secondary-grey;
    margin-bottom: 24px;
    padding: 0 15px;
    text-align: center;

    @include breakpoint-above('medium') {
      padding: 0 42px;
    }
  }

  .account-action {
    margin: 0 10px;

    @include breakpoint-above('medium') {
      margin: 0 20px;
    }
  }

  &__link {
    text-align: center;
  }
}

.students-waiting {
  padding: 0;

  @include breakpoint-above('medium') {
    padding: 0 30px;
  }
}

.volunteer-impact {
  padding: 0 10px;

  @include breakpoint-above('medium') {
    padding: 0 30px;
  }

  &__stats {
    width: 100%;
    padding: 10px 5px 0;
  }

  &__stat-label {
    text-align: left;
  }

  &__stat {
    display: flex;
    justify-content: space-between;
    padding: 15px 0;
    font-size: 16px;
  }

  &__stat-value {
    font-weight: bold;
    text-align: right;
  }

  &__last-updated {
    width: 100%;
    text-align: right;
    padding: 10px 0;
    font-size: 12px;
  }
}

.dashboard-notice {
  padding: 15px;
  background: $c-warning-orange;
  border-radius: 8px;
  margin: 20px 0 -20px;
  font-weight: 500;
  font-size: 16px;

  a {
    color: #fff;

    &:hover {
      color: #f3f3f3;
      text-decoration: none;
    }
  }

  &--info {
    color: #fff;
    background-color: $c-information-blue;
  }
}

.notifications-button {
  @include flex-container(row, flex-end);
  margin-bottom: 1.4em;
}

.track-hours-link {
  @include font-category('button');
  display: inline-flex;
  align-items: center;
  margin: 0 auto;
  padding: 0;
  color: $c-success-green;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

.arrow-icon {
  fill: $c-success-green;
  height: 16px;
  width: 16px;
  margin-top: 2px;
  margin-left: 8px;

  &--banner {
    height: 16px;
    width: 16px;
    fill: $upchieve-white;
    margin-left: 0.5em;
  }
}

.rejoin-session-container {
  text-align: center;
}

.loader--center {
  text-align: center;
}

html.is-rtl {
  .volunteer-dashboard {
    &__body {
      @include child-spacing-rtl(right, 0);

      @include breakpoint-above('huge') {
        @include child-spacing-rtl(right, 40px);
      }
    }
  }

  .volunteer-impact {
    &__stat-label {
      text-align: right;
    }

    &__stat-value {
      text-align: left;
    }

    &__last-updated {
      text-align: left;
    }
  }

  .arrow-icon {
    margin-right: 8px;
    margin-left: unset;

    &--banner {
      margin-right: 0.5em;
      margin-left: unset;
    }
  }
}
</style>
